import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MdAdd,
  MdArrowForward,
  MdEdit,
  MdDelete,
  MdPeople,
  MdSearch,
} from "react-icons/md";
import {
  API_BASE_URL,
  getAuthHeaders,
  getJsonAuthHeaders,
} from "../../../utils/api";
import { isConfiguredCampusClass } from "../../../utils/campusClasses";
import { getStoredCampusSections } from "../../../utils/campusSections";
import { confirmDialog } from "../../../utils/dialogs";
import { C, Card, CardHeader, TH, TD, Badge, Btn } from "../../shared/fees/feeUI";
import { StatCard, StatGrid, BackButton } from "../../shared/reports/reportsShared";

const labelSt = { display: "block", fontSize: "11px", fontWeight: 700, color: C.slate, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" };
const inputSt = { padding: "9px 13px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", color: C.text, background: C.white, outline: "none", boxSizing: "border-box" };

const classSectionsOf = (classItem) => {
  const values = Array.isArray(classItem?.sections) && classItem.sections.length
    ? classItem.sections
    : classItem?.section
      ? [classItem.section]
      : [];
  return [...new Set(values.map((value) => String(value || "").trim().toUpperCase()).filter(Boolean))];
};

const classSessionIdsOf = (classItem) => {
  const values = Array.isArray(classItem?.session_ids) && classItem.session_ids.length
    ? classItem.session_ids
    : classItem?.session_id
      ? [classItem.session_id]
      : [];
  return [...new Set(values.map((value) => String(value || "")).filter(Boolean))];
};

function ClassManagement({ campusId, campusName, onBack, showToast, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperadmin =
    (localStorage.getItem("als_role") || "").toLowerCase() === "superadmin";
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [campusSections, setCampusSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sessionFilter, setSessionFilter] = useState(
    searchParams.get("session_id") || "",
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingClassId, setDeletingClassId] = useState(null);
  const [newClass, setNewClass] = useState({
    name: "",
    section: "",
    session_id: "",
  });
  const [classForm, setClassForm] = useState({
    name: "",
    section: "",
    session_id: "",
  });
  const selectedSessionParam = searchParams.get("session_id") || "";

  useEffect(() => {
    setSessionFilter(selectedSessionParam);
  }, [selectedSessionParam]);

  const getErrorMessage = useCallback(async (res, fallback) => {
    const data = await res.json().catch(() => ({}));
    if (Array.isArray(data.detail)) {
      return data.detail.map((err) => err.msg).join(", ") || fallback;
    }
    return data.detail || fallback;
  }, []);

  const loadSessions = useCallback(async () => {
    if (!campusId) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/sessions?campus_id=${campusId}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, "Could not load sessions."));
      }

      const data = await res.json();
      setSessions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load sessions", error);
      showToast?.("error", "Load Error", error.message || "Could not load sessions.");
      setSessions([]);
    }
  }, [campusId, getErrorMessage, onLogout, showToast]);

  const loadClasses = useCallback(async () => {
    if (!campusId) return;

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/classes?campus_id=${campusId}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, "Could not load classes."));
      }

      const data = await res.json();
      setClasses(Array.isArray(data) ? data.filter(isConfiguredCampusClass) : []);
    } catch (error) {
      console.error("Failed to load classes", error);
      showToast?.("error", "Load Error", error.message || "Could not load classes.");
      setClasses([]);
    } finally {
      setIsLoading(false);
    }
  }, [campusId, getErrorMessage, onLogout, showToast]);

  useEffect(() => {
    loadSessions();
    loadClasses();
  }, [loadSessions, loadClasses]);

  useEffect(() => {
    const refreshCampusSections = () => {
      setCampusSections(getStoredCampusSections(campusId));
    };

    refreshCampusSections();
    window.addEventListener("campus-sections-updated", refreshCampusSections);
    window.addEventListener("storage", refreshCampusSections);
    return () => {
      window.removeEventListener("campus-sections-updated", refreshCampusSections);
      window.removeEventListener("storage", refreshCampusSections);
    };
  }, [campusId]);

  const resetNewClass = () => {
    setNewClass({ name: "", section: "", session_id: "" });
  };

  const resetClassForm = () => {
    setClassForm({ name: "", section: "", session_id: "" });
  };

  const classPayload = (data) => ({
    campus_id: parseInt(campusId, 10),
    session_id: data.session_id ? parseInt(data.session_id, 10) : null,
    name: data.name.trim(),
    section: data.section?.trim() || null,
  });

  const handleCreateClass = async (e) => {
    e.preventDefault();

    if (!newClass.name.trim()) {
      showToast?.("error", "Error", "Class name is required.");
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/classes`, {
        method: "POST",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify(classPayload(newClass)),
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, "Could not create class."));
      }

      showToast?.("success", "Class Created", `${newClass.name.trim()} has been added.`);
      setShowAddModal(false);
      resetNewClass();
      await loadClasses();
    } catch (error) {
      console.error("Failed to create class", error);
      showToast?.("error", "Create Error", error.message || "Could not create class.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    if (!editingClass) return;

    if (!classForm.name.trim()) {
      showToast?.("error", "Error", "Class name is required.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = classPayload(classForm);
      delete payload.campus_id;

      const res = await fetch(`${API_BASE_URL}/api/classes/${editingClass.id}`, {
        method: "PUT",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, "Could not update class."));
      }

      showToast?.("success", "Class Updated", "Class details have been updated.");
      setEditingClass(null);
      resetClassForm();
      await loadClasses();
    } catch (error) {
      console.error("Failed to update class", error);
      showToast?.("error", "Update Error", error.message || "Could not update class.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (
      !(await confirmDialog(
        "Are you sure you want to delete this class?",
        { title: "Delete Class", confirmLabel: "Delete Class", variant: "danger" },
      ))
    ) return;

    setDeletingClassId(classId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/classes/${classId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, "Could not delete class."));
      }

      showToast?.("success", "Class Deleted", "Class removed successfully.");
      await loadClasses();
    } catch (error) {
      console.error("Failed to delete class", error);
      showToast?.("error", "Delete Error", error.message || "Could not delete class.");
    } finally {
      setDeletingClassId(null);
    }
  };

  const sessionNameById = useMemo(() => {
    return new Map(sessions.map((session) => [String(session.id), session.name]));
  }, [sessions]);

  const sectionOptions = useMemo(() => {
    const existingSections = classes.flatMap((classItem) => classSectionsOf(classItem));
    return [...new Set([...campusSections, ...existingSections])].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [campusSections, classes]);

  const getSessionName = (sessionId) => {
    if (!sessionId) return "No Session";
    const session = sessionNameById.get(String(sessionId));
    return session || "Unknown Session";
  };

  const handleSessionFilterChange = (value) => {
    setSessionFilter(value);
    setSearchParams(value ? { session_id: value } : {});
  };

  const filteredClasses = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return classes.filter((classItem) => {
      const sessionIds = classSessionIdsOf(classItem);
      const sectionNames = classSectionsOf(classItem);
      const matchesSession =
        !sessionFilter ||
        (sessionFilter === "unassigned" && sessionIds.length === 0) ||
        sessionIds.includes(String(sessionFilter));

      const searchableText = [
        classItem.name,
        ...sectionNames,
        ...sessionIds.map((id) => sessionNameById.get(String(id))),
        classItem.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesSession && searchableText.includes(normalizedSearch);
    });
  }, [classes, searchTerm, sessionFilter, sessionNameById]);

  const linkedClassCount = classes.filter((classItem) => classSessionIdsOf(classItem).length > 0).length;
  const unassignedClassCount = classes.length - linkedClassCount;
  const sectionCount = new Set(
    classes.flatMap((classItem) => classSectionsOf(classItem)),
  ).size;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
        <BackButton onClick={onBack} label="Back" />
        <div style={{ marginLeft: "auto" }}>
          <Btn onClick={() => setShowAddModal(true)} disabled={isSaving}>
            <MdAdd /> Add Class
          </Btn>
        </div>
      </div>

      <StatGrid>
        <StatCard label="Total" value={classes.length} accent={C.navy} />
        <StatCard label="Linked" value={linkedClassCount} accent={C.green} />
        <StatCard label="Sections" value={sectionCount} accent={C.slateLight} />
      </StatGrid>

      <Card style={{ marginBottom: "16px" }}>
        <div style={{ padding: "16px 20px", display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 260px" }}>
            <label style={labelSt}>Search</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: C.slateLight, display: "flex", pointerEvents: "none" }}>
                <MdSearch />
              </span>
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search classes"
                style={{ ...inputSt, width: "100%", paddingLeft: "36px" }}
              />
            </div>
          </div>

          <div style={{ minWidth: "220px" }}>
            <label style={labelSt}>Session</label>
            <select value={sessionFilter} onChange={(e) => handleSessionFilterChange(e.target.value)} style={inputSt}>
              <option value="">All sessions</option>
              <option value="unassigned">Unassigned classes: {unassignedClassCount}</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>{session.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Classes"
          right={<Badge color="slate">{filteredClasses.length} of {classes.length} shown</Badge>}
        />

        {filteredClasses.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <TH>Class</TH>
                  <TH>Section</TH>
                  <TH>Session</TH>
                  <TH>Class ID</TH>
                  <TH right>Actions</TH>
                </tr>
              </thead>
              <tbody>
                {filteredClasses.map((classItem, i) => (
                  <tr key={classItem.id} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
                    <TD><Badge color="blue">{classItem.name}</Badge></TD>
                    <TD>
                      {classSectionsOf(classItem).length ? (
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          {classSectionsOf(classItem).map((section) => (
                            <Badge key={`${classItem.id}-section-${section}`} color="slate">Section {section}</Badge>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: C.slateLight }}>Unassigned</span>
                      )}
                    </TD>
                    <TD>
                      {classSessionIdsOf(classItem).length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {classSessionIdsOf(classItem).map((sessionId) => (
                            <span key={`${classItem.id}-session-${sessionId}`}>
                              {getSessionName(sessionId)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: C.slateLight }}>No Session</span>
                      )}
                    </TD>
                    <TD mono>{classItem.id}</TD>
                    <TD right>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <Btn
                          size="sm"
                          variant="blue"
                          onClick={() => navigate(`/superadmin/campuses/${campusId}/classes/${classItem.id}`)}
                        >
                          <MdPeople /> View Students <MdArrowForward />
                        </Btn>
                        <Btn
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingClass(classItem);
                            setClassForm({
                              name: classItem.name,
                              section: classItem.section || "",
                              session_id: classItem.session_id || "",
                            });
                          }}
                        >
                          <MdEdit /> Edit
                        </Btn>
                        {isSuperadmin && (
                          <Btn
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteClass(classItem.id)}
                            disabled={deletingClassId === classItem.id}
                          >
                            <MdDelete /> {deletingClassId === classItem.id ? "Deleting..." : "Delete"}
                          </Btn>
                        )}
                      </div>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isLoading && classes.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>Loading classes</div>
            <p style={{ fontSize: "13px" }}>Loading classes...</p>
          </div>
        )}
        {!isLoading && classes.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No classes yet</div>
            <p style={{ fontSize: "13px" }}>Create the first class for this campus.</p>
          </div>
        )}
        {!isLoading && classes.length > 0 && filteredClasses.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No matching classes</div>
            <p style={{ fontSize: "13px" }}>Try a different search or session filter.</p>
          </div>
        )}
      </Card>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content campus-form-modal">
            <div className="modal-header">
              <h3>Add New Class</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateClass}>
                <div className="reg-form-group">
                  <label className="reg-form-label">Class Name *</label>
                  <input
                    type="text"
                    className="reg-input"
                    value={newClass.name}
                    onChange={(e) =>
                      setNewClass({ ...newClass, name: e.target.value })
                    }
                    required
                    placeholder="e.g., Class 10"
                  />
                </div>
                <p style={{ marginTop: 12, color: "#56637a", fontSize: 13 }}>
                  Section and session assignment can be completed in class setup after the class is created.
                </p>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowAddModal(false)}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSaving}>
                    {isSaving ? "Creating..." : "Create Class"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {editingClass && (
        <div className="modal-overlay">
          <div className="modal-content campus-form-modal">
            <div className="modal-header">
              <h3>Edit Class</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setEditingClass(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateClass}>
                <div className="reg-form-group">
                  <label className="reg-form-label">Class Name *</label>
                  <input
                    type="text"
                    className="reg-input"
                    value={classForm.name}
                    onChange={(e) =>
                      setClassForm({ ...classForm, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="reg-form-group">
                  <label className="reg-form-label">Section</label>
                  <select
                    className="reg-input"
                    value={classForm.section}
                    onChange={(e) =>
                      setClassForm({ ...classForm, section: e.target.value })
                    }
                  >
                    <option value="">-- No Section --</option>
                    {sectionOptions.map((section) => (
                      <option key={section} value={section}>
                        Section {section}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="reg-form-group">
                  <label className="reg-form-label">
                    Assign to Session
                  </label>
                  <select
                    className="reg-input"
                    value={classForm.session_id}
                    onChange={(e) =>
                      setClassForm({ ...classForm, session_id: e.target.value })
                    }
                  >
                    <option value="">-- Select Session --</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setEditingClass(null)}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSaving}>
                    {isSaving ? "Updating..." : "Update Class"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassManagement;
